import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';

const AZ_LABELS = {
  abnahme: 'Abnahme', gemeinde: 'Abstimmung Gemeinde', aufmass: 'Aufmaß',
  hausanschluesse: 'Hausanschlüsse', jourfixe: 'Jour Fixe', kontrolle: 'Kontrolle',
  mitverfolgung: 'Mitverfolgung', planung: 'Planung', rechnungen: 'Rechnungen',
  sonstiges: 'Sonstiges', baufirma: 'Baufirma', zwischenbericht: 'Zwischenbericht',
};

function azTaetigkeiten(json) {
  try {
    const t = JSON.parse(json || '{}');
    return Object.entries(t).filter(([k, v]) => v && k !== 'freitext').map(([k]) => AZ_LABELS[k] || k).join(', ');
  } catch { return ''; }
}

function azFreitext(json) {
  try { return JSON.parse(json || '{}').freitext || ''; } catch { return ''; }
}

function azDauer(beginn, ende) {
  if (!beginn || !ende) return '–';
  const [h1, m1] = beginn.split(':').map(Number);
  const [h2, m2] = ende.split(':').map(Number);
  const diff = (h2 * 60 + m2) - (h1 * 60 + m1);
  if (diff <= 0) return '–';
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}min`;
}

function getKW(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
  const w1 = new Date(d.getFullYear(), 0, 4);
  return 'KW ' + (1 + Math.round(((d - w1) / 86400000 - 3 + (w1.getDay() + 6) % 7) / 7));
}

function getKWRange(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = dt => dt.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  return `${fmt(monday)} – ${fmt(sunday)}`;
}

function getMondayDieserWoche() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return d.toISOString().slice(0, 10);
}

function parseMin(t) {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  return isNaN(h) || isNaN(m) ? null : h * 60 + m;
}

function mergeIntervals(ivs) {
  if (!ivs.length) return [];
  ivs.sort((a, b) => a[0] - b[0]);
  const out = [[...ivs[0]]];
  for (let i = 1; i < ivs.length; i++) {
    const last = out[out.length - 1];
    if (ivs[i][0] <= last[1]) last[1] = Math.max(last[1], ivs[i][1]);
    else out.push([...ivs[i]]);
  }
  return out;
}

function calcMergedMin(entries) {
  // merge intervals per person+day, return total minutes
  const map = {};
  for (const b of entries) {
    const s = parseMin(b.arbeitsbeginn);
    const e = parseMin(b.arbeitsende);
    if (s === null || e === null || e <= s) continue;
    const key = `${b.erstellt_von_name}|${b.datum?.slice(0, 10)}`;
    if (!map[key]) map[key] = [];
    map[key].push([s, e]);
  }
  let total = 0;
  for (const ivs of Object.values(map)) {
    mergeIntervals(ivs).forEach(([s, e]) => { total += e - s; });
  }
  return total;
}

function fmtMin(min) {
  if (!min || min <= 0) return '–';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}min`;
}

function fmtTage(min) {
  if (!min || min <= 0) return '0';
  return (min / 480).toFixed(2).replace('.', ',');
}

function typLabel(typ) {
  if (typ === 'jourfixe') return 'Jour Fixe';
  if (typ === 'arbeitszeit') return 'Arbeitszeit';
  return 'Zwischenbericht';
}

function AuthImage({ src, alt, style, onClick }) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem('bau_token');
    let url = null;
    fetch(src, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => { url = URL.createObjectURL(blob); setBlobUrl(url); })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [src]);
  if (!blobUrl) return <div style={{ ...style, background: 'var(--bg-3)', borderRadius: 6 }} />;
  return <img src={blobUrl} alt={alt} style={style} onClick={onClick} />;
}

export default function Admin() {
  const navigate = useNavigate();
  const [nutzer, setNutzer] = useState([]);
  const [fttbauNutzer, setFttbauNutzer] = useState([]);
  const [projekte, setProjekte] = useState([]);
  const [teilnehmer, setTeilnehmer] = useState([]);
  const [aktivProjekt, setAktivProjekt] = useState(null);
  const [tab, setTab] = useState('nutzer');
  const [newTeilnehmer, setNewTeilnehmer] = useState('');
  const [wetterConfig, setWetterConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [alleBerichte, setAlleBerichte] = useState(null);
  const [offeneKWs, setOffeneKWs] = useState({});
  const [alleFotos, setAlleFotos] = useState(null);
  const [fotoLightbox, setFotoLightbox] = useState(null);
  const [fotoFilter, setFotoFilter] = useState('alle');
  const [ausgeblendet, setAusgeblendet] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get('/nutzer'),
      api.get('/nutzer/fttbau-users').catch(() => []),
      api.get('/nutzer/fttbau-projekte').catch(() => []),
      api.get('/wetter').catch(() => []),
      api.get('/nutzer/projekt-sichtbarkeit').catch(() => []),
    ]).then(([n, fu, p, wc, ausg]) => {
      setNutzer(n); setFttbauNutzer(fu); setProjekte(p); setAusgeblendet(ausg);
      const wcMap = {};
      wc.forEach(w => { wcMap[w.projekt_id] = w; });
      setWetterConfig(wcMap);
      if (p.length) setAktivProjekt(p[0]);
      setLoading(false);
    });
  }, []);

  const toggleProjektSichtbar = async (p) => {
    const istAus = ausgeblendet.includes(p.id);
    try {
      await api.post('/nutzer/projekt-sichtbarkeit', { projekt_id: p.id, sichtbar: istAus });
      setAusgeblendet(prev => istAus ? prev.filter(id => id !== p.id) : [...prev, p.id]);
    } catch (e) { alert('Fehler: ' + e.message); }
  };

  useEffect(() => {
    if (aktivProjekt) api.get(`/nutzer/teilnehmer/${aktivProjekt.id}`).then(setTeilnehmer);
  }, [aktivProjekt]);

  useEffect(() => {
    if (tab === 'arbeitszeit' && alleBerichte === null) {
      api.get('/berichte').then(all => {
        const mitZeit = all
          .filter(b => b.arbeitsbeginn && b.arbeitsende)
          .sort((a, b) => (b.datum > a.datum ? 1 : -1));
        setAlleBerichte(mitZeit);
      });
    }
    if (tab === 'fotos' && alleFotos === null) {
      api.get('/bilder').then(setAlleFotos).catch(() => setAlleFotos([]));
    }
  }, [tab]);

  const handleZugang = async (fu) => {
    await api.post('/nutzer', { user_id: fu.id, name: fu.name, email: fu.email, ist_admin: false });
    const n = await api.get('/nutzer');
    setNutzer(n);
  };

  const handleEntzug = async (id) => {
    await api.delete(`/nutzer/${id}`);
    const n = await api.get('/nutzer');
    setNutzer(n);
  };

  const handleTeilnehmerAdd = async () => {
    if (!newTeilnehmer.trim() || !aktivProjekt) return;
    const t = await api.post('/nutzer/teilnehmer', { projekt_id: aktivProjekt.id, name: newTeilnehmer.trim() });
    setTeilnehmer(prev => [...prev, t]);
    setNewTeilnehmer('');
  };

  const handleTeilnehmerDelete = async (id) => {
    await api.delete(`/nutzer/teilnehmer/${id}`);
    setTeilnehmer(prev => prev.filter(t => t.id !== id));
  };

  const hatZugang = (fu) => nutzer.some(n => n.user_id === fu.id && n.aktiv);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>;

  // --- Arbeitszeit-Tab Berechnungen ---
  const mondayStr = getMondayDieserWoche();

  // Alle Nutzer mit Zugang (für Widgets)
  const aktivNutzer = nutzer.filter(n => n.aktiv);

  // Berichte dieser Woche
  const dieseWoche = (alleBerichte || []).filter(b => b.datum?.slice(0, 10) >= mondayStr);

  // KW-Gruppen
  const kwGruppen = [];
  let letzteKW = null;
  for (const b of (alleBerichte || [])) {
    const kw = getKW(b.datum);
    if (kw !== letzteKW) { kwGruppen.push({ kw, eintraege: [] }); letzteKW = kw; }
    kwGruppen[kwGruppen.length - 1].eintraege.push(b);
  }

  return (
    <div style={{ minHeight: '100vh' }}>
      <div style={{ background: 'var(--bg-2)', borderBottom: '1px solid var(--border)', padding: '0 16px', display: 'flex', alignItems: 'center', height: 52, gap: 10 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/')}>← Zurück</button>
        <div style={{ fontWeight: 700 }}>Bautagebuch Admin</div>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
        <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: 'var(--bg-2)', padding: 4, borderRadius: 10, border: '1px solid var(--border)', width: 'fit-content' }}>
          {[{ id: 'nutzer', label: 'Zugänge' }, { id: 'projekte', label: 'Projekte' }, { id: 'teilnehmer', label: 'Teilnehmer' }, { id: 'wetter', label: 'Wetter' }, { id: 'arbeitszeit', label: 'Arbeitszeit' }, { id: 'fotos', label: 'Fotos' }].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className="btn btn-sm"
              style={{ background: tab === t.id ? 'var(--bg-3)' : 'transparent', border: 'none', color: tab === t.id ? 'var(--text)' : 'var(--text-3)' }}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'nutzer' && (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%' }}>
              <thead><tr><th style={{ textAlign: 'left', padding: '10px 16px', fontSize: '0.75rem', color: 'var(--text-3)' }}>Name</th><th>E-Mail</th><th>Zugang</th></tr></thead>
              <tbody>
                {fttbauNutzer.map(fu => (
                  <tr key={fu.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 16px', fontWeight: 500 }}>{fu.name}</td>
                    <td style={{ padding: '10px 16px', fontSize: '0.8rem', color: 'var(--text-3)' }}>{fu.email}</td>
                    <td style={{ padding: '10px 16px' }}>
                      {hatZugang(fu) ? (
                        <button className="btn btn-danger btn-sm" onClick={() => handleEntzug(fu.id)}>Entziehen</button>
                      ) : (
                        <button className="btn btn-secondary btn-sm" onClick={() => handleZugang(fu)}>Zugang geben</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'projekte' && (
          <div>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-3)', marginBottom: 16 }}>
              Ausgeblendete Projekte erscheinen weder in den Bautagebüchern/Berichten noch auf der Foto-Karte.
            </p>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%' }}>
                <tbody>
                  {[...projekte].sort((a, b) => a.name.localeCompare(b.name, 'de')).map(p => {
                    const istAus = ausgeblendet.includes(p.id);
                    return (
                      <tr key={p.id} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '12px 16px', fontWeight: 500, color: istAus ? 'var(--text-3)' : 'var(--text)' }}>
                          {p.name}
                          {istAus && <span style={{ marginLeft: 8, fontSize: '0.72rem', color: 'var(--text-3)' }}>· ausgeblendet</span>}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                          <button className={istAus ? 'btn btn-secondary btn-sm' : 'btn btn-ghost btn-sm'} onClick={() => toggleProjektSichtbar(p)}>
                            {istAus ? 'Einblenden' : 'Ausblenden'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'wetter' && (
          <div>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-3)', marginBottom: 16 }}>
              Wetterstandort pro Projekt – wird beim Klick auf „Aktuell laden" im Bericht verwendet.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {projekte.map(p => {
                const cfg = wetterConfig[p.id] || {};
                const save = async () => {
                  if (!cfg.wetter_ort || !cfg.wetter_lat || !cfg.wetter_lng) return alert('Alle Felder ausfüllen');
                  await api.put(`/wetter/${p.id}`, { wetter_ort: cfg.wetter_ort, wetter_lat: parseFloat(cfg.wetter_lat), wetter_lng: parseFloat(cfg.wetter_lng) });
                  alert('Gespeichert');
                };
                const upd = (key, val) => setWetterConfig(prev => ({ ...prev, [p.id]: { ...prev[p.id], [key]: val } }));
                return (
                  <div key={p.id} className="card" style={{ padding: 16 }}>
                    <h3 style={{ marginBottom: 12 }}>{p.name}</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 8, alignItems: 'end' }}>
                      <div className="form-group">
                        <label className="form-label">Ort</label>
                        <input className="form-input" placeholder="z.B. Villingen-Schwenningen" value={cfg.wetter_ort || ''} onChange={e => upd('wetter_ort', e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Breitengrad</label>
                        <input className="form-input" placeholder="48.0608" value={cfg.wetter_lat || ''} onChange={e => upd('wetter_lat', e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Längengrad</label>
                        <input className="form-input" placeholder="8.4539" value={cfg.wetter_lng || ''} onChange={e => upd('wetter_lng', e.target.value)} />
                      </div>
                      <button className="btn btn-primary btn-sm" onClick={save} style={{ marginBottom: 1 }}>Speichern</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === 'teilnehmer' && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {projekte.map(p => (
                <button key={p.id} className="btn btn-sm"
                  onClick={() => setAktivProjekt(p)}
                  style={{ background: aktivProjekt?.id === p.id ? 'var(--accent)' : 'var(--bg-2)', color: aktivProjekt?.id === p.id ? '#fff' : 'var(--text-2)', border: '1px solid var(--border)' }}>
                  {p.name}
                </button>
              ))}
            </div>

            {aktivProjekt && (
              <div className="card" style={{ padding: 20 }}>
                <h3 style={{ marginBottom: 14 }}>Teilnehmer · {aktivProjekt.name}</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                  {teilnehmer.map(t => (
                    <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ flex: 1 }}>{t.name}</span>
                      <button className="btn btn-danger btn-sm" onClick={() => handleTeilnehmerDelete(t.id)}>×</button>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="form-input" placeholder="Name des Teilnehmers…" value={newTeilnehmer}
                    onChange={e => setNewTeilnehmer(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleTeilnehmerAdd()} />
                  <button className="btn btn-primary" onClick={handleTeilnehmerAdd}>Hinzufügen</button>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'arbeitszeit' && (
          <div>
            {alleBerichte === null ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" /></div>
            ) : (
              <>
                {/* Nutzer-Widgets aktuelle KW */}
                <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', color: 'var(--text-3)', textTransform: 'uppercase', marginBottom: 10 }}>
                  Aktuelle Woche · {getKW(mondayStr)}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(aktivNutzer.length, 3)}, 1fr)`, gap: 12, marginBottom: 28 }}>
                  {aktivNutzer.map(n => {
                    const eintraege = dieseWoche.filter(b => b.erstellt_von_name === n.name);
                    const min = calcMergedMin(eintraege);
                    const tage = fmtTage(min);
                    const stunden = fmtMin(min);
                    const firstName = n.name.split(' ')[0];
                    return (
                      <div key={n.id} className="card" style={{ padding: '18px 20px' }}>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: 6 }}>{firstName}</div>
                        <div style={{ fontSize: '2rem', fontWeight: 800, color: min > 0 ? 'var(--accent)' : 'var(--text-3)', lineHeight: 1.1 }}>
                          {tage} <span style={{ fontSize: '1rem', fontWeight: 400 }}>T</span>
                        </div>
                        <div style={{ fontSize: '0.82rem', color: 'var(--text-2)', marginTop: 4 }}>{stunden}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Aufklappbare KW-Bereiche */}
                {kwGruppen.length === 0 ? (
                  <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>
                    Noch keine Arbeitszeiten erfasst
                  </div>
                ) : kwGruppen.map(g => {
                  const isOffen = !!offeneKWs[g.kw];
                  const kwMin = calcMergedMin(g.eintraege);
                  const kwTage = fmtTage(kwMin);
                  const kwStunden = fmtMin(kwMin);
                  return (
                    <div key={g.kw} style={{ marginBottom: 8 }}>
                      <button
                        onClick={() => setOffeneKWs(prev => ({ ...prev, [g.kw]: !prev[g.kw] }))}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                          background: 'var(--bg-2)', border: '1px solid var(--border)',
                          borderRadius: isOffen ? '10px 10px 0 0' : 10,
                          padding: '12px 16px', cursor: 'pointer', textAlign: 'left',
                        }}
                      >
                        <span style={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.08em', color: 'var(--text-3)', textTransform: 'uppercase', minWidth: 48 }}>{g.kw}</span>
                        <span style={{ fontSize: '0.78rem', color: 'var(--text-3)' }}>{getKWRange(g.eintraege[0].datum)}</span>
                        <span style={{ fontWeight: 700, color: 'var(--text)', fontSize: '1rem' }}>{kwTage} T</span>
                        <span style={{ color: 'var(--text-3)', fontSize: '0.85rem' }}>({kwStunden})</span>
                        <span style={{ marginLeft: 'auto', color: 'var(--text-3)', fontSize: '0.9rem' }}>{isOffen ? '▲' : '▼'}</span>
                      </button>

                      {isOffen && (
                        <div className="card" style={{ padding: 0, overflow: 'hidden', borderRadius: '0 0 10px 10px', borderTop: 'none' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                            <thead>
                              <tr style={{ background: 'var(--bg-3)' }}>
                                {['Datum', 'Person', 'Projekt', 'Typ', 'Zeit', 'Dauer', 'Tätigkeiten', 'Anmerkung'].map(h => (
                                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: '0.72rem', color: 'var(--text-3)', fontWeight: 700, letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {g.eintraege.map(az => {
                                const tags = azTaetigkeiten(az.taetigkeit);
                                const freitext = azFreitext(az.taetigkeit);
                                return (
                                  <tr key={az.id} style={{ borderTop: '1px solid var(--border)' }}>
                                    <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                                      {new Date(az.datum).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                                    </td>
                                    <td style={{ padding: '9px 12px', fontWeight: 600, whiteSpace: 'nowrap' }}>{az.erstellt_von_name}</td>
                                    <td style={{ padding: '9px 12px', color: 'var(--text-2)' }}>{az.projekt_name}</td>
                                    <td style={{ padding: '9px 12px', color: 'var(--text-3)', whiteSpace: 'nowrap', fontSize: '0.78rem' }}>{typLabel(az.typ)}</td>
                                    <td style={{ padding: '9px 12px', fontFamily: 'var(--font-mono, monospace)', whiteSpace: 'nowrap' }}>
                                      {az.arbeitsbeginn && az.arbeitsende ? `${az.arbeitsbeginn}–${az.arbeitsende}` : '–'}
                                    </td>
                                    <td style={{ padding: '9px 12px', fontWeight: 700, color: 'var(--accent)', whiteSpace: 'nowrap' }}>
                                      {azDauer(az.arbeitsbeginn, az.arbeitsende)}
                                    </td>
                                    <td style={{ padding: '9px 12px', color: 'var(--text-2)' }}>{tags || '–'}</td>
                                    <td style={{ padding: '9px 12px', color: 'var(--text-3)', fontStyle: freitext ? 'normal' : 'italic' }}>{freitext || '–'}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}

        {tab === 'fotos' && (
          <div>
            {alleFotos === null ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" /></div>
            ) : alleFotos.length === 0 ? (
              <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>
                Noch keine Fotos vorhanden
              </div>
            ) : (() => {
              // Projektliste in Reihenfolge des neuesten Fotos
              const projektNamen = [];
              const gesehen = {};
              for (const foto of alleFotos) {
                if (!gesehen[foto.projekt_name]) { gesehen[foto.projekt_name] = true; projektNamen.push(foto.projekt_name); }
              }

              const gezeigteGruppen = fotoFilter === 'alle'
                ? projektNamen.map(name => ({ name, fotos: alleFotos.filter(f => f.projekt_name === name) }))
                : [{ name: fotoFilter, fotos: alleFotos.filter(f => f.projekt_name === fotoFilter) }];

              return (
                <>
                  {/* Filterleiste */}
                  <div style={{ display: 'flex', gap: 4, marginBottom: 24, flexWrap: 'wrap', background: 'var(--bg-2)', padding: 4, borderRadius: 10, border: '1px solid var(--border)', width: 'fit-content' }}>
                    {['alle', ...projektNamen].map(name => (
                      <button key={name} onClick={() => setFotoFilter(name)} className="btn btn-sm"
                        style={{ background: fotoFilter === name ? 'var(--bg-3)' : 'transparent', border: 'none', color: fotoFilter === name ? 'var(--text)' : 'var(--text-3)' }}>
                        {name === 'alle' ? 'Alle Bilder' : name}
                      </button>
                    ))}
                  </div>

                  {/* Gruppen */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
                    {gezeigteGruppen.map(gruppe => (
                      <div key={gruppe.name}>
                        {fotoFilter === 'alle' && (
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
                            <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', color: 'var(--text-3)', textTransform: 'uppercase' }}>
                              {gruppe.name}
                            </div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-3)' }}>
                              {gruppe.fotos.length} Foto{gruppe.fotos.length !== 1 ? 's' : ''}
                            </div>
                          </div>
                        )}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
                          {gruppe.fotos.map(foto => (
                            <div key={foto.id} className="card" style={{ padding: 0, overflow: 'hidden', cursor: 'pointer' }}
                              onClick={() => setFotoLightbox(foto)}>
                              <AuthImage
                                src={`/api/uploads/${foto.dateiname}`}
                                alt={foto.beschriftung || ''}
                                style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block' }}
                              />
                              <div style={{ padding: '7px 10px' }}>
                                {foto.beschriftung && (
                                  <div style={{ fontSize: '0.78rem', color: 'var(--text)', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {foto.beschriftung}
                                  </div>
                                )}
                                <div style={{ fontSize: '0.72rem', color: 'var(--text-3)' }}>
                                  {new Date(foto.datum).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                                  {' · '}{foto.erstellt_von_name}
                                  {fotoFilter === 'alle' ? '' : ` · ${foto.projekt_name}`}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}

            {fotoLightbox && (
              <div onClick={() => setFotoLightbox(null)} style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20,
              }}>
                <div onClick={e => e.stopPropagation()} style={{ maxWidth: 900, width: '100%' }}>
                  <AuthImage
                    src={`/api/uploads/${fotoLightbox.dateiname}`}
                    alt={fotoLightbox.beschriftung || ''}
                    style={{ width: '100%', maxHeight: '75vh', objectFit: 'contain', borderRadius: 8, display: 'block' }}
                  />
                  <div style={{ marginTop: 12, color: '#fff' }}>
                    {fotoLightbox.beschriftung && (
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>{fotoLightbox.beschriftung}</div>
                    )}
                    <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.6)' }}>
                      {new Date(fotoLightbox.datum).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}
                      {' · '}{fotoLightbox.erstellt_von_name}{' · '}{fotoLightbox.projekt_name}
                    </div>
                  </div>
                  <button onClick={() => setFotoLightbox(null)} style={{
                    position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.15)',
                    border: 'none', color: '#fff', borderRadius: '50%', width: 36, height: 36,
                    fontSize: '1.2rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>×</button>
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
